import { isVisibilityRule, type Finding, type EvaluationStatus, type Section } from '@eformulieren/model'
import { type CheckContext, onSection, sectionCheckPasses, fieldCheckPasses, type FieldCheck } from './check.js'
import { CHARACTER_CHECK, hasForbiddenCharacter } from './characters.js'
import { noticeFor } from './notice.js'
import { rowsOf, type State, textsOf } from './state.js'
import { type FieldPlace, fieldsIndex } from './fields.js'
import { determineVisibility, type Visibility } from './visibility.js'

/**
 * De evaluator: van een vorm plus een stand naar de status uit ontwerp 9.2.
 *
 * Twee stappen, in deze volgorde (ontwerp 5.6): eerst de zichtbaarheid van alle
 * secties en velden, daarna de controles, alleen over wat zichtbaar is. Een
 * verborgen veld doet niet mee aan validatie (R-34) en een antwoord in een
 * verborgen sectie blijft wel staan (R-39): dat laatste is hier niets doen, want
 * de evaluator raakt de stand niet aan.
 *
 * Hard is een poort op indienen, waarschuwing is advies en blokkeert nooit
 * (R-23, R-31). Dat onderscheid zit uitsluitend hier: de controles zelf weten
 * alleen of ze zijn gehaald.
 *
 * Deze functie is de enige implementatie, gebruikt door elk pad dat leest of
 * schrijft: de browser voor directe terugkoppeling (R-01b) en de pod bij elke
 * wijziging en bij indienen (R-01c, R-01f). Een tweede kopie voor een ander
 * kanaal is een fout en geen optimalisatie (ontwerp 5.2).
 */

export interface Evaluation {
  /** De status zoals de pod hem teruggeeft (ontwerp 9.2). */
  readonly status: EvaluationStatus
  /** Wat er zichtbaar is; de renderer heeft dat naast de status nodig. */
  readonly zichtbaarheid: Visibility
  /**
   * Per rijveld de rijen waarin een regel faalt, met de bevindingen per rij (R-140).
   *
   * Naast de status en niet erin, om dezelfde reden als `zichtbaarheid`: de status is het formaat
   * dat de pod teruggeeft (ontwerp 9.2) en is op veld-id gesleuteld, zonder rijdimensie. De
   * renderer heeft die dimensie wel nodig, want een melding hoort bij de cel waarin hij geldt en
   * een link in het overzicht van openstaande controles hoort naar die cel te springen. Alleen
   * rijen met een bevinding staan erin; een rijveld zonder falende rij heeft geen sleutel.
   *
   * De telling verandert er niet door: een regel op een rijveld is één openstaande controle, in
   * hoeveel rijen hij ook faalt.
   */
  readonly rijen: Readonly<Record<string, Readonly<Record<string, RowFindings>>>>
}

/** De bevindingen van één rijveld in één rij. */
export interface RowFindings {
  readonly hardeControles: readonly Finding[]
  readonly waarschuwingen: readonly Finding[]
}

type FieldStatus = EvaluationStatus['veld'][string]
type SectionStatus = EvaluationStatus['sectie'][string]
type RowState = { hardeControles: Finding[]; waarschuwingen: Finding[] }

export function evaluate(sections: readonly Section[], state: State, context: CheckContext = {}): Evaluation {
  // Zonder verwijzingen in de context is elke verwijzing leeg (R-63). De pod en de browser geven ze altijd
  // mee (`referenceContext`, `FillForm`); alleen een test of een formulier zonder verwijzingen laat ze weg.
  const visibility = determineVisibility(sections, state, context.verwijzingen ?? {})
  const index = fieldsIndex(sections)

  const field: Record<string, FieldStatus> = {}
  const section: Record<string, SectionStatus> = {}
  const rows: Record<string, Record<string, RowState>> = {}
  let outstandingTotal = 0

  for (const current of sections) {
    // Een verborgen sectie staat niet in de status: er is niets te tonen en niets
    // te valideren (R-34, ontwerp 5.6).
    if (!visibility.secties.has(current.id)) continue

    const visibleFields = [...index]
      .filter(([id, place]) => place.sectieId === current.id && visibility.velden.has(id))
      .map(([id]) => id)
    for (const id of visibleFields) field[id] = { status: 'geldig', hardeControles: [], waarschuwingen: [] }

    let outstandingHere = 0
    const ownHard: Finding[] = []
    const ownWarnings: Finding[] = []

    for (const rule of current.regels) {
      // Zichtbaarheid is geen controle en is in stap één al verwerkt.
      if (isVisibilityRule(rule)) continue

      // Het doelveld gaat mee, zodat de standaardmelding van verplicht het label noemt (audit R-27).
      const targetField = onSection(rule) ? undefined : index.get(rule.doelVeld)?.veld
      const finding: Finding = {
        regelId: rule.id,
        melding: noticeFor(rule, targetField === undefined ? undefined : { control: targetField.control, label: 'label' in targetField ? targetField.label : undefined }),
      }

      if (onSection(rule)) {
        if (sectionCheckPasses(rule, sectionTexts(current, state, visibility))) continue
        if (rule.mechanisme === 'hard') {
          ownHard.push(finding)
          outstandingHere += 1
        } else {
          ownWarnings.push(finding)
        }
        continue
      }

      const target = field[rule.doelVeld]
      // Het doel is verborgen, of het staat niet in deze projectie. In beide
      // gevallen valideert het niet.
      if (target === undefined) continue

      const failing = failingRows(rule, state, index.get(rule.doelVeld), context)
      if (failing.length === 0) continue
      if (rule.mechanisme === 'hard') {
        target.hardeControles.push(finding)
        target.status = 'ongeldig'
        outstandingHere += 1
      } else {
        target.waarschuwingen.push(finding)
      }
      for (const rowId of failing) {
        if (rowId === NO_ROW) continue
        const perRow = (rows[rule.doelVeld] ??= {})
        const own = (perRow[rowId] ??= { hardeControles: [], waarschuwingen: [] })
        if (rule.mechanisme === 'hard') own.hardeControles.push(finding)
        else own.waarschuwingen.push(finding)
      }
    }

    // De tekenset geldt voor elk zichtbaar veld, met of zonder regel (`characters.ts`). Een herhaalgroep
    // zelf heeft geen tekst; zijn rijvelden wel, en die staan hier elk met hun eigen id.
    for (const id of visibleFields) {
      const place = index.get(id)
      if (place === undefined || place.veld.control === 'herhaalgroep') continue
      const failing = place.herhaalgroepId === undefined
        ? (hasForbiddenCharacter(state[id]) ? [NO_ROW] : [])
        : (rowsOf(state[place.herhaalgroepId]) ?? []).filter((row) => hasForbiddenCharacter(row.waarden[id])).map((row) => row.rijId)
      if (failing.length === 0) continue
      const target = field[id]
      if (target === undefined) continue
      target.hardeControles.push(CHARACTER_CHECK)
      target.status = 'ongeldig'
      outstandingHere += 1
      for (const rowId of failing) {
        if (rowId === NO_ROW) continue
        const perRow = (rows[id] ??= {})
        const own = (perRow[rowId] ??= { hardeControles: [], waarschuwingen: [] })
        own.hardeControles.push(CHARACTER_CHECK)
      }
    }

    section[current.id] = {
      status: outstandingHere === 0 ? 'volledig' : 'onvolledig',
      openstaandeHardeControles: outstandingHere,
      hardeControles: ownHard,
      waarschuwingen: ownWarnings,
    }
    outstandingTotal += outstandingHere
  }

  return {
    zichtbaarheid: visibility,
    rijen: rows,
    status: {
      veld: field,
      sectie: section,
      formulier: {
        // Indienen kan niet zolang een harde controle faalt (R-23, R-31). Een
        // waarschuwing weegt hier per definitie niet mee.
        indienbaar: outstandingTotal === 0,
        openstaandeHardeControles: outstandingTotal,
      },
    },
  }
}

/** Het teken voor "faalt, en het is geen rijveld": een gewoon veld heeft geen rij. */
const NO_ROW = ''

/**
 * Rekent een veldcontrole uit, en voor een rijveld doet hij dat per rij.
 *
 * Geeft terug waar de regel faalt: leeg als hij voldoet, `[GEEN_RIJ]` als een gewoon veld
 * faalt, en de rij-id's waarin hij faalt bij een rijveld. Eén functie voor beide vragen (faalt
 * hij, en waar), zodat de status en de rijdimensie niet uit twee berekeningen komen die uit de
 * pas kunnen lopen.
 *
 * Per rij geldt dezelfde veldset met dezelfde validatie per veld (R-41), dus een
 * regel op een rijveld faalt zodra hij in één rij faalt. Welke rij dat is staat
 * niet in de status: `EvaluatieStatus` is op veld-id gesleuteld en heeft geen
 * rijdimensie (ontwerp 9.2). De renderer heeft die dimensie wel nodig om een melding
 * aan een cel te koppelen (R-140); die krijgt hij uit `Evaluatie.rijen`, naast de status.
 *
 * Een herhaalgroep zonder rijen laat een regel op zijn rijvelden slagen: er is
 * niets ingevuld om te toetsen. Dat een groep rijen moet hebben is
 * `minimumAantal` op de groep zelf (R-42).
 */
function failingRows(rule: FieldCheck, state: State, place: FieldPlace | undefined, context: CheckContext): readonly string[] {
  const repeatGroupId = place?.herhaalgroepId
  if (repeatGroupId === undefined) return fieldCheckPasses(rule, state[rule.doelVeld], context) ? [] : [NO_ROW]
  const rows = rowsOf(state[repeatGroupId]) ?? []
  return rows.filter((row) => !fieldCheckPasses(rule, row.waarden[rule.doelVeld], context)).map((row) => row.rijId)
}

/**
 * De teksten van een sectie, voor de woordendrempel van `korteSectie`.
 *
 * Alleen uit zichtbare velden, en binnen een herhaalgroep alleen uit zichtbare
 * rijvelden: een verborgen kolom telt niet mee (R-34).
 */
function sectionTexts(section: Section, state: State, visibility: Visibility): readonly string[] {
  const out: string[] = []
  for (const field of section.velden) {
    if (!visibility.velden.has(field.id)) continue
    if (field.control === 'herhaalgroep') {
      const rows = rowsOf(state[field.id]) ?? []
      for (const row of rows) {
        for (const rowField of field.rijVelden) {
          if (!visibility.velden.has(rowField.id)) continue
          out.push(...textsOf(row.waarden[rowField.id]))
        }
      }
      continue
    }
    out.push(...textsOf(state[field.id]))
  }
  return out
}
